/**
 * "In other systems": the records in other systems one strategy object (an
 * Objective, a Goal) is linked to and kept in step with, with Link and Unlink.
 * A Link control is drawn only for a system the workspace is connected to.
 * Used by the Objectives page's "Where this objective lives" and the Goal
 * dialog. Canon 01 › Field Source, Sync Direction and Conflict Rule.
 */

import { useEffect, useState } from 'react';
import { ExternalLink, Link2, Search, X } from 'lucide-react';
import { useExternalLinks, externalLinkDisplayKey } from '../../../hooks/prioritization/useItemExternalLinks';
import { linkStrategyObject, linkSystemsFor, unlinkStrategyObject } from '../../../lib/strategyObjectLinks';
import {
  linearSearchInitiatives,
  linearStatus,
  type LinearInitiativeSummary,
} from '../../../lib/semanticConnectors/linearSemanticApi';
import { getIntegrationsApi, type JiraProject } from '../../../lib/integrationsApi';
import type { SyncedObjectType } from '../../../../supabase/functions/_shared/syncedObjects.ts';

const SYSTEM_NAMES: Record<string, string> = { linear: 'Linear', jira: 'Jira' };

interface Candidate {
  externalId: string;
  label: string;
  title: string;
  url?: string;
  theirValues: Record<string, unknown>;
}

function LinkPicker({ workspaceId, type, connectorType, onPick, onClose }: {
  workspaceId: string;
  type: SyncedObjectType;
  connectorType: string;
  onPick: (c: Candidate) => Promise<void>;
  onClose: () => void;
}) {
  const [q, setQ] = useState('');
  const [projects, setProjects] = useState<JiraProject[]>([]);
  const [projectKey, setProjectKey] = useState('');
  const [results, setResults] = useState<Candidate[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (connectorType !== 'jira') return;
    getIntegrationsApi(workspaceId).listJiraProjects()
      .then((p) => { setProjects(p); if (p[0]) setProjectKey(p[0].key); })
      .catch((e) => setError(e instanceof Error ? e.message : 'Jira did not answer'));
  }, [connectorType, workspaceId]);

  useEffect(() => {
    if (connectorType === 'jira' && !projectKey) return;
    const t = setTimeout(() => {
      setResults(null);
      setError(null);
      const run: Promise<Candidate[]> = connectorType === 'linear'
        ? linearSearchInitiatives(workspaceId, q).then((list: LinearInitiativeSummary[]) => list.map((i) => ({
            externalId: i.id,
            label: i.name,
            title: i.status,
            url: i.url,
            theirValues: { name: i.name, description: i.description ?? '' },
          })))
        : getIntegrationsApi(workspaceId).searchJiraIssues(projectKey, { search: q, maxResults: 25 }).then((list) => list.map((i) => ({
            externalId: i.key,
            label: `${i.key} · ${i.summary}`,
            title: `${i.issueType} · ${i.status}`,
            url: i.url,
            theirValues: { name: i.summary },
          })));
      run.then(setResults).catch((e) => { setResults([]); setError(e instanceof Error ? e.message : 'The search failed'); });
    }, 300);
    return () => clearTimeout(t);
  }, [connectorType, workspaceId, q, projectKey]);

  const recordLabel = linkSystemsFor(type).find((s) => s.connectorType === connectorType)?.recordLabel ?? 'record';

  return (
    <div className="border border-[var(--rule)] bg-[var(--surface)] p-3 space-y-2">
      <div className="flex items-center gap-2">
        <p className="text-xs font-medium text-[var(--text-sec)] flex-1">Link a {recordLabel}</p>
        <button type="button" onClick={onClose} className="p-1 text-[var(--text-ter)] hover:text-[var(--text-sec)]" title="Close"><X className="h-3.5 w-3.5" /></button>
      </div>
      <div className="flex flex-wrap gap-2">
        {connectorType === 'jira' && projects.length > 0 && (
          <select value={projectKey} onChange={(e) => setProjectKey(e.target.value)} className="text-xs border border-[var(--rule)] bg-[var(--bg-raised)] px-2 py-1">
            {projects.map((p) => <option key={p.key} value={p.key}>{p.key} · {p.name}</option>)}
          </select>
        )}
        <div className="flex items-center gap-1 flex-1 min-w-[10rem] border border-[var(--rule)] bg-[var(--bg-raised)] px-2">
          <Search className="h-3.5 w-3.5 text-[var(--text-ter)]" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by name" className="flex-1 text-xs py-1 bg-transparent outline-none" />
        </div>
      </div>
      {error && <p className="text-xs text-[var(--coral)]">{error}</p>}
      {results === null ? (
        <p className="text-xs text-[var(--text-ter)]">Searching…</p>
      ) : results.length === 0 ? (
        !error && <p className="text-xs text-[var(--text-ter)]">Nothing matches.</p>
      ) : (
        <ul className="max-h-56 overflow-y-auto divide-y divide-[var(--rule)]">
          {results.map((c) => (
            <li key={c.externalId}>
              <button
                type="button"
                disabled={saving}
                onClick={async () => { setSaving(true); try { await onPick(c); } finally { setSaving(false); } }}
                className="w-full text-left px-2 py-1.5 hover:bg-[var(--hover-tint)] disabled:opacity-50"
              >
                <span className="block text-xs text-[var(--text-pri)] truncate">{c.label}</span>
                <span className="block text-[11px] text-[var(--text-ter)]">{c.title}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export function LinkedElsewhere({ workspaceId, type, id, values }: {
  workspaceId: string;
  type: SyncedObjectType;
  id: string;
  /** Our value of each carried field (name, description), to show where the other system differs. */
  values: Record<string, string>;
}) {
  const links = useExternalLinks(workspaceId, type, id);
  const systems = linkSystemsFor(type);
  const [connected, setConnected] = useState<Record<string, boolean>>({});
  const [picking, setPicking] = useState<string | null>(null);
  const [confirmUnlink, setConfirmUnlink] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Which systems this workspace is connected to: only those get a "Link" control.
  useEffect(() => {
    let live = true;
    linearStatus(workspaceId).then((s) => live && setConnected((c) => ({ ...c, linear: !!s.connected }))).catch(() => live && setConnected((c) => ({ ...c, linear: false })));
    getIntegrationsApi(workspaceId).listJiraProjects().then(() => live && setConnected((c) => ({ ...c, jira: true }))).catch(() => live && setConnected((c) => ({ ...c, jira: false })));
    return () => { live = false; };
  }, [workspaceId]);

  const linkable = systems.filter((s) => connected[s.connectorType]);

  return (
      <div className="space-y-1">
        <p className="text-xs font-medium text-[var(--text-sec)]">In other systems</p>
        {links === null ? (
          <p className="text-xs text-[var(--text-ter)]">Reading…</p>
        ) : links.length === 0 ? (
          <p className="text-xs text-[var(--text-ter)]">Not linked to any other system.</p>
        ) : (
          <ul className="space-y-2">
            {links.map((l) => {
              const sys = systems.find((s) => s.connectorType === l.connectorType);
              const name = SYSTEM_NAMES[l.connectorType] ?? l.connectorType;
              const key = `${l.connectorType}:${l.externalId}`;
              const differences = Object.keys(sys?.fields ?? {}).filter((f) => {
                const theirs = l.externalValues?.[f];
                return theirs !== undefined && String(theirs ?? '').trim() !== String(values[f] ?? '').trim();
              });
              return (
                <li key={key} className="text-xs space-y-0.5">
                  <div className="flex flex-wrap items-center gap-2">
                    <Link2 className="h-3.5 w-3.5 text-[var(--text-ter)]" />
                    <span className="text-[var(--text-ter)]">{sys?.recordLabel ?? name}</span>
                    {l.externalUrl ? (
                      <a href={l.externalUrl} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-[var(--text-pri)] hover:text-primary-600">
                        {externalLinkDisplayKey(l)} <ExternalLink className="h-3 w-3" />
                      </a>
                    ) : (
                      <span className="text-[var(--text-pri)]">{externalLinkDisplayKey(l)}</span>
                    )}
                    {confirmUnlink === key ? (
                      <span className="inline-flex items-center gap-1">
                        <button type="button" className="text-[var(--coral)] hover:underline" onClick={async () => {
                          const r = await unlinkStrategyObject({ workspaceId, type, id, connectorType: l.connectorType, externalId: l.externalId });
                          setError(r.error);
                          setConfirmUnlink(null);
                        }}>Unlink</button>
                        <button type="button" className="text-[var(--text-ter)] hover:underline" onClick={() => setConfirmUnlink(null)}>Keep</button>
                      </span>
                    ) : (
                      <button type="button" className="text-[var(--text-ter)] hover:text-[var(--coral)]" onClick={() => setConfirmUnlink(key)}>Unlink…</button>
                    )}
                  </div>
                  <p className="text-[var(--text-ter)] pl-5">
                    Its name{sys?.fields.description ? ' and description' : ''} are kept in step with {name}, under {name}&rsquo;s Field Sync Rules.
                  </p>
                  {differences.map((f) => (
                    <p key={f} className="pl-5 text-[var(--mustard)]">
                      {name} says the {f} is &ldquo;{String(l.externalValues?.[f] ?? '')}&rdquo;.
                    </p>
                  ))}
                </li>
              );
            })}
          </ul>
        )}
        {error && <p className="text-xs text-[var(--coral)]">{error}</p>}
        {picking ? (
          <LinkPicker
            workspaceId={workspaceId}
            type={type}
            connectorType={picking}
            onClose={() => setPicking(null)}
            onPick={async (c) => {
              const r = await linkStrategyObject({
                workspaceId,
                type,
                id,
                connectorType: picking,
                externalId: c.externalId,
                externalUrl: c.url,
                externalLabel: c.label,
                theirValues: c.theirValues,
              });
              setError(r.error);
              if (!r.error) setPicking(null);
            }}
          />
        ) : linkable.length > 0 ? (
          <div className="flex flex-wrap gap-3 pt-1">
            {linkable.map((s) => (
              <button key={s.connectorType} type="button" onClick={() => setPicking(s.connectorType)} className="text-xs text-primary-600 hover:underline">
                + Link a {s.recordLabel}
              </button>
            ))}
          </div>
        ) : null}
      </div>
  );
}
